import { useMemo } from 'react'
import { continentToCountries, REGION_LABEL, CONTINENT_NAMES } from '@/constants'
import { COUNTRY_FILL } from '@/views/worldView/styles'

type Sev = 0 | 1 | 2

interface Args {
  dimension: string
  nodes: readonly unknown[]
}

function clampSev(v: unknown): Sev | null {
  return (v === 0 || v === 1 || v === 2) ? (v as Sev) : null
}

function mkRule(selector: string, sev: Sev | null, active: boolean): string {
  if (!selector) return ''
  if (!active || sev == null) {
    return `${selector} { fill: ${COUNTRY_FILL.inactive.fill}; fill-opacity: 1; stroke: ${COUNTRY_FILL.inactive.stroke}; stroke-width: 0.7; }`
  }
  return `${selector} { fill: ${COUNTRY_FILL.active.fill(sev)}; fill-opacity: 1; stroke: ${COUNTRY_FILL.active.stroke(sev)}; stroke-width: 0.7; }`
}

function normContinentDisplayName(idOrLabel: string): string | null {
  const raw = String(idOrLabel ?? '').trim()
  if (!raw) return null
  const up = raw.toUpperCase()
  // Continent codes only — REGION_LABEL also has country demos (GB/ID/…) which
  // must not be treated as continents.
  const CONTINENT_CODES = new Set(['EU', 'NA', 'SA', 'AS', 'AF', 'OC', 'OT', 'AN', 'ME'])
  if (CONTINENT_CODES.has(up) && REGION_LABEL[up as keyof typeof REGION_LABEL]) {
    return REGION_LABEL[up as keyof typeof REGION_LABEL]!
  }
  if (CONTINENT_NAMES.includes(raw)) return raw
  const hit = CONTINENT_NAMES.find((n) => n.toLowerCase() === raw.toLowerCase())
  return hit ?? null
}

export function useCountryStyles({ dimension, nodes }: Args) {
  return useMemo(() => {
    if (dimension !== 'continent' && dimension !== 'country') {
      return { continentCss: '', countryCss: '' }
    }

    const list = Array.isArray(nodes) ? nodes : []
    if (!list.length) return { continentCss: '', countryCss: '' }

    if (dimension === 'continent') {
      const rules: string[] = []
      for (const raw of list) {
        const n = raw as Record<string, unknown>
        const key = String(n.value ?? n.label ?? '').trim()
        const disp = normContinentDisplayName(key)
        if (!disp) continue
        const countries = continentToCountries(disp)
        if (!countries.length) continue
        const selector = countries.map((cc) => `.world-land path#${cc}`).join(', ')
        const sev = clampSev(n.avgSeverity)
        const active = Number(n.totalStreams ?? 0) > 0
        rules.push(mkRule(selector, sev, active))
      }
      return { continentCss: rules.join('\n'), countryCss: '' }
    }

    const rules: string[] = []
    for (const raw of list) {
      const n = raw as Record<string, unknown>
      const rawId = String(n.value ?? n.label ?? '').trim().toUpperCase()
      const cc = rawId.length === 2 ? rawId : null
      if (!cc) continue
      const sev = clampSev(n.avgSeverity)
      const active = Number(n.totalStreams ?? 0) > 0
      rules.push(mkRule(`.world-land path#${cc}`, sev, active))
    }
    return { continentCss: '', countryCss: rules.join('\n') }
  }, [nodes, dimension])
}
